/**
 * Crew helpers, shared by every surface that edits who is on a work order
 * and the hours they carry, off the record's assignees pivot: the workflow's
 * Job Details panel, the "hours worked" prompt at hand-off (the sign-off
 * step, and the submit-for-verification confirm), and the assign-from-the-
 * list dialog. One reading of the pivot, one definition of "did anything
 * change".
 */
import type { WorkOrder } from '../services/workOrderService';

/**
 * Pull the per-person hours off the assignees pivot, keyed by user id. Values
 * are kept as strings so an input can sit empty mid-typing without coercing
 * to 0.
 */
export function readAssigneeHours(workOrder: WorkOrder): Record<string, string> {
	const hours: Record<string, string> = {};
	(workOrder.assignees ?? []).forEach((a) => {
		const value = a.pivot?.allocatedHours;
		if (value != null && value !== '') hours[String(a.id)] = String(value);
	});
	return hours;
}

/** Compare two hours maps numerically, so "8" and "8.0" match and a row
 *  cleared to empty reads as different from one that held a number. */
export function sameHours(a: Record<string, string>, b: Record<string, string>): boolean {
	const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
	for (const key of Array.from(keys)) {
		const left = a[key];
		const right = b[key];
		const leftEmpty = left === undefined || left === '';
		const rightEmpty = right === undefined || right === '';
		if (leftEmpty !== rightEmpty) return false;
		if (!leftEmpty && Number(left) !== Number(right)) return false;
	}
	return true;
}

/**
 * Pull the additional (non-primary) assignee ids off the eager-loaded
 * assignees relation. MySQL hands the pivot boolean back as 0/1, so the
 * primary test coerces rather than trusting truthiness — and the record's
 * own assigneeId is excluded as well, so a pivot row that lost its flag can
 * never list the primary as their own helper.
 */
export function readAdditionalAssigneeIds(workOrder: WorkOrder): string[] {
	const primaryId = workOrder.assigneeId != null ? String(workOrder.assigneeId) : '';
	return (workOrder.assignees ?? [])
		.filter((a) => !Number(a.pivot?.isPrimary ?? 0))
		.map((a) => String(a.id))
		.filter((id) => id !== primaryId);
}

/** Order-insensitive comparison of two id lists — the crew is a set, and
 *  adding then removing the same person is not a change. */
export function sameIdSet(a: string[], b: string[]): boolean {
	if (a.length !== b.length) return false;
	const left = [...a].sort();
	const right = [...b].sort();
	return left.every((id, i) => id === right[i]);
}
